import React from 'react';
import { useTranslation } from 'react-i18next';
import { assigneeAvatarColor } from '../../utils/assigneeHelpers';

export interface AssigneeAvatarItem {
  id: string;
  name?: string;
  initials?: string;
}

interface AssigneeAvatarsProps {
  assignees: AssigneeAvatarItem[];
  max?: number;
  size?: 'sm' | 'md';
  onClick?: () => void;
  className?: string;
}

const sizePx = { sm: 28, md: 32 } as const;

const AssigneeAvatars: React.FC<AssigneeAvatarsProps> = ({
  assignees,
  max = 3,
  size = 'sm',
  onClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const px = sizePx[size];

  if (assignees.length === 0) {
    return (
      <span className={`tasks-assignee-unassigned${className ? ` ${className}` : ''}`}>
        {t('tasks_unassigned')}
      </span>
    );
  }

  const visible = assignees.slice(0, max);
  const overflow = assignees.length - max;
  const names = assignees
    .map((a) => a.name?.trim())
    .filter((n): n is string => Boolean(n))
    .join(', ');
  const label = names || t('tasks_assignees');

  const content = (
    <div className="tasks-assignee-avatars" aria-hidden={onClick ? undefined : true}>
      {visible.map((assignee, index) => (
        <span
          key={assignee.id}
          className="tasks-assignee-avatar"
          style={{
            width: px,
            height: px,
            fontSize: px <= 28 ? '0.65rem' : '0.7rem',
            backgroundColor: assigneeAvatarColor(assignee.id),
            zIndex: visible.length - index,
          }}
        >
          {assignee.initials?.trim() || '?'}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="tasks-assignee-avatar tasks-assignee-avatar--more"
          style={{ width: px, height: px, fontSize: '0.65rem' }}
        >
          +{overflow}
        </span>
      )}
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`tasks-assignee-avatars-btn${className ? ` ${className}` : ''}`}
        onClick={onClick}
        title={label}
        aria-label={label}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={`tasks-assignee-avatars-wrap${className ? ` ${className}` : ''}`}
      title={label}
      aria-label={label}
    >
      {content}
    </div>
  );
};

export default AssigneeAvatars;
